/**
 * Provides APIs for accessing and manipulating the messages from Redux store.
 * These APIs can be used for different purposes e.g. validation messages, nextwork failure messages etc.
 * @module MessageManager
 */
/*
 * Copyright (c) 2020 Pegasystems Inc.
 * All rights reserved.
 */
import type { Draft } from '@reduxjs/toolkit';

import { getChildContainers, getActiveContainerItemName } from '../container/container-utils';
import * as StateManager from '../store/state/state-manager';
import { CLEAR_MESSAGES, ADD_MESSAGES } from '../store/state/actions';
import Utils from '../utils/utils';
import constants, { publicConstants } from '../constants';
import ContextTreeManager from '../contextree/treemanager';
import type { Action } from '../store/state/state-manager';
import CaseUtils from '../case/utils';
import environmentInfo from '../environment-info';
import type { LocalizationService } from '../locale/LocalizationService';

import type { AllErrorMessages, ErrorMessages, MessagesConfigObject, MessagesObject } from './types';

export const MESSAGES_PATH = {
  CONTENT_FIELDS_PATH: [constants.CONTEXT_DATA, constants.CASE_INFO.CASE_INFO, constants.CONTENT],
  DATA_OBJECT_CONTENT_FIELDS_PATH: [constants.CONTEXT_DATA, constants.DATA_INFO.DATA_INFO, constants.CONTENT],
  OBJECT_CONTENT_FIELDS_PATH: [constants.CONTEXT_DATA, constants.OBJECT_INFO.OBJECT_INFO, constants.CONTENT],
  FIELD_MESSAGES_PATH: [
    constants.CONTEXT_DATA,
    constants.CASE_INFO.CASE_INFO,
    constants.CONTENT,
    constants.SUMMARY_OF_MESSAGES,
    constants.PROPERTY_MESSAGES
  ],
  DATA_OBJECT_FIELD_MESSAGES_PATH: [
    constants.CONTEXT_DATA,
    constants.DATA_INFO.DATA_INFO,
    constants.CONTENT,
    constants.SUMMARY_OF_MESSAGES,
    constants.PROPERTY_MESSAGES
  ],
  OBJECT_FIELD_MESSAGES_PATH: [
    constants.CONTEXT_DATA,
    constants.OBJECT_INFO.OBJECT_INFO,
    constants.CONTENT,
    constants.SUMMARY_OF_MESSAGES,
    constants.PROPERTY_MESSAGES
  ],
  PAGE_MESSAGES_PATH: [constants.CONTEXT_DATA, constants.SUMMARY_OF_MESSAGES, constants.PAGE_MESSAGES],
  HTTP_MESSAGES_PATH: [constants.CONTEXT_DATA, constants.SUMMARY_OF_MESSAGES, constants.HTTP_MESSAGES],
  CASE_MESSAGE_PATH: [constants.CASEMESSAGES]
};

/**
 * @function
 */

const getPropertyRef = (property: string) => {
  if (property?.startsWith('.')) {
    return property.substring(1).split('.');
  }
  return property.split('.');
};

/**
 * this function helps to get corresponding message path based on property and category params
 * @function getMessagesPath
 * @private
 * @param property - property name
 * @param pageReference - page reference of the property
 * @param category - category of message
 * @param isListEntry - is list entry
 * @returns - path
 */
export const getMessagesPath = (property = '', pageReference = '', category?: string, isListEntry?: boolean) => {
  let path: string[] = [];
  let propertyRef: string[] = [];
  if (property !== null) {
    propertyRef = getPropertyRef(property);
  }
  const pageRef = pageReference && pageReference.startsWith('.') ? pageReference.substr(1) : pageReference;
  if ((category === constants.PROPERTY && property) || property) {
    path = [constants.CONTEXT_DATA, ...pageRef.split('.')];
    for (let i = 0; i < propertyRef.length - 1; i += 1) {
      // to add all the embedded object name
      path.push(propertyRef[i]);
    }
    path.push(constants.SUMMARY_OF_MESSAGES, constants.PROPERTY_MESSAGES, propertyRef[propertyRef.length - 1]);
  } else if (category === publicConstants.PAGE) {
    path = [...MESSAGES_PATH.PAGE_MESSAGES_PATH];
  } else if (category === constants.HTTP) {
    path = [...MESSAGES_PATH.HTTP_MESSAGES_PATH];
  } else if (category === constants.CASEMESSAGES) {
    path = [...MESSAGES_PATH.CASE_MESSAGE_PATH];
  } else if (isListEntry) {
    path = [constants.CONTEXT_DATA, ...pageRef.split('.')];
  }
  return path.filter(Boolean);
};

export const getTableRowErrorMessages = (
  allErrorMessages: AllErrorMessages,
  referenceList: string,
  rowIndex: number
) => {
  const rowErrors: AllErrorMessages = { content: {} };
  // Need to ignore caseInfo, content hierachy while getting table name
  const tableList = referenceList.split('.').slice(2).join('.');
  const rowKey = `${tableList}[${rowIndex}]`;
  const errors = Utils.getIn(allErrorMessages?.content, rowKey.split('.'));
  if (errors) {
    Utils.setIn(rowErrors.content, rowKey.split('.'), { ...errors });
  }
  return rowErrors;
};

const getAllErrors = (contextDataObj: object, listName: string, refPath: string): string[] => {
  const dataObject = Utils.getIn(contextDataObj, [constants.CONTEXT_DATA, ...refPath.split('.')], {});
  return Object.keys(dataObject).filter((key) => key.includes(`${listName}[`));
};

const getFilteredErrors = (allErrors: string[], listIndex: number, listMoveToIndex: number) => {
  if (listIndex < listMoveToIndex) {
    return allErrors.filter(
      (item: string) =>
        parseInt(item.match(/\d+/)?.[0] as string, 10) > listIndex &&
        parseInt(item.match(/\d+/)?.[0] as string, 10) <= listMoveToIndex
    );
  }
  return allErrors.filter(
    (item) =>
      parseInt(item.match(/\d+/)?.[0] as string, 10) < listIndex &&
      parseInt(item.match(/\d+/)?.[0] as string, 10) >= listMoveToIndex
  );
};

const processErrors = (
  draft: Draft<Record<string, any>>,
  context: string,
  allErrors: string[],
  listIndex: number,
  listMoveToIndex: number,
  referenceList: string,
  resultErrors: ErrorMessages = {}
) => {
  const filteredErrors: string[] = getFilteredErrors(allErrors, listIndex, listMoveToIndex);
  const direction = listIndex > listMoveToIndex ? 'up' : 'down';

  if (filteredErrors && filteredErrors?.length > 0) {
    filteredErrors?.forEach((item) => {
      const index = parseInt(item.match(/\d+/)?.[0] as string, 10);
      const errorPath = `${referenceList}[${index}]`;
      const offset = direction === 'up' ? 1 : -1;
      resultErrors[`${referenceList}[${index + offset}]`] = Utils.getIn(draft[context], [
        constants.CONTEXT_DATA,
        ...errorPath.split('.')
      ]);
      Utils.deleteIn(draft[context], [constants.CONTEXT_DATA, ...errorPath.split('.')]);
    });
  }
  const errorsList = Object.keys(resultErrors);

  if (errorsList.length > 0) {
    errorsList.forEach((item) => {
      Utils.setIn(draft[context], [constants.CONTEXT_DATA, ...item.split('.')], resultErrors[item]);
    });
  }
};

const updateReferencePathList = (referenceList: string[]): string => {
  const result = [];
  let i = 0;

  while (i < referenceList.length) {
    if (i + 1 < referenceList.length && !Number.isNaN(parseInt(referenceList[i + 1], 10))) {
      result.push(`${referenceList[i]}[${referenceList[i + 1]}]`);
      i += 2;
    } else {
      result.push(referenceList[i]);
      i += 1;
    }
  }
  return result.join('.');
};

export const reorderMessages = (
  draft: Draft<Record<string, any>>,
  context: string,
  referenceList: string,
  listIndex: number,
  listMoveToIndex: number
) => {
  const contextDataObj = Utils.deepClone(Utils.getIn(draft, [`${context}`]));
  const dataObject = Utils.getIn(contextDataObj, [constants.CONTEXT_DATA, constants.CASE_INFO.CASE_INFO]);
  if (!dataObject) {
    return;
  }
  const updatedReferenceList = updateReferencePathList(referenceList.split('.'));
  const refPath = updatedReferenceList.substring(0, updatedReferenceList.lastIndexOf('.'));
  const listName = updatedReferenceList.substring(updatedReferenceList.lastIndexOf('.') + 1);
  const allErrors = getAllErrors(contextDataObj, listName, refPath);
  if (allErrors.length === 0) {
    return;
  }
  const listIndexErrors = getTableRowErrorMessages(dataObject, updatedReferenceList, listIndex).content;

  if (listIndexErrors && Object.keys(listIndexErrors).length > 0) {
    const resultErrors: ErrorMessages = {};

    const errorItem = `${updatedReferenceList}[${listIndex}]`;
    const existingErrors = Utils.getIn(contextDataObj, [constants.CONTEXT_DATA, ...errorItem.split('.')]);
    resultErrors[`${updatedReferenceList}[${listMoveToIndex}]`] = existingErrors;
    Utils.deleteIn(draft[context], [constants.CONTEXT_DATA, ...errorItem.split('.')]);
    processErrors(draft, context, allErrors, listIndex, listMoveToIndex, updatedReferenceList, resultErrors);
  } else {
    processErrors(draft, context, allErrors, listIndex, listMoveToIndex, updatedReferenceList);
  }
};

/**
 * this function helps to retrieve messages from context object
 * @function getMessages
 * @public
 * @param config object which has information to process the messages information
 * @returns - messages object
 *
 * @example <caption>Example for retrieving property messages.</caption>
 * property - required
 * context - required
 * type - optional
 * category - not required
 * getMessages({
 *    type: 'error',
 *    property: '.firstName',
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for retrieving http messages.</caption>
 * category - required
 * context - required
 * type - optional
 * getMessages({
 *    type: 'error',
 *    category: 'HTTP'
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for retrieving page messages.</caption>
 * category - required
 * context - required
 * type - optional
 * getMessages({
 *    type: 'error',
 *    category: 'PAGE'
 *    context: 'app/primary_2'
 * });
 */
export const getMessages = (config: Omit<MessagesConfigObject, 'context'> & { context: string | null }) => {
  const { type, property, category, context, pageReference } = config;
  let propertyWithoutDot;
  if (property) {
    propertyWithoutDot = property.slice(1);
  }

  const storeDataObj = StateManager.getDataObject(context);
  let messages: any[] = [];
  const allMessages = Utils.getIn(storeDataObj, getMessagesPath(propertyWithoutDot, pageReference, category));

  if (!type || type === 'all') {
    return allMessages;
  }

  if (allMessages && Array.isArray(allMessages) && allMessages.length > 0) {
    messages = allMessages.filter((msg) => msg.type === type);
  }
  if (type === publicConstants.MESSAGES.MESSAGES_TYPE_ERROR && messages.length > 0 && property) {
    ContextTreeManager.mutateField(context, pageReference, property, {
      messages: [...messages],
      type: publicConstants.MESSAGES.MESSAGES_TYPE_ERROR,
      fieldName: property
    });
  }

  return messages;
};

/**
 * this function helps to clear validation messages on property,
 * http messages on context/page.
 *
 * @function clearMessages
 * @public
 * @param config object which has information to process the messages information
 * @example <caption>Example for clearing property messages.</caption>
 * clearMessages({
 *    type: 'error',
 *    property: '.firstName',
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for clearing http messages.</caption>
 * clearMessages({
 *    type: 'error',
 *    category: 'HTTP'
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for clearing page messages.</caption>
 * clearMessages({
 *    type: 'info',
 *    category: 'PAGE'
 *    context: 'app/primary_2'
 * });
 *
 *
 */
export const clearMessages = (config: MessagesConfigObject): void => {
  const {
    property,
    pageReference,
    category,
    context,
    type,
    subType,
    isListEntry,
    clearContextMessages,
    deletingIndex
  } = config;
  const payload: Action['payload'] = { context, type, subType };
  if (property && !isListEntry) {
    ContextTreeManager.mutateField(context, pageReference, property, {
      type: CLEAR_MESSAGES,
      fieldName: property
    });
    payload.property = property;
    payload.pageReference = pageReference;
  } else if (isListEntry) {
    payload.pageReference = pageReference;
    payload.isListEntry = isListEntry;
    payload.deletingIndex = deletingIndex;
  } else payload.category = category;
  if (clearContextMessages) {
    StateManager.dispatch({
      type: CLEAR_MESSAGES,
      payload: {
        stateObj: config.stateObj,
        context
      }
    });
  } else {
    StateManager.dispatch({
      type: CLEAR_MESSAGES,
      payload
    });
  }
};

/**
 * this function helps to add validation messages on property,
 * http messages on context/page.
 * @function addMessages
 * @public
 * @param config object which has information to process the messages information
 * @example <caption>Example for add http messages.</caption>
 * addMessages({
 *    messages: [
 *      {
 *        type: 'error',
 *        message: 'Validation Error'
 *      }
 *    ],
 *    category: 'HTTP'
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for add page messages.</caption>
 * addMessages({
 *    messages: [
 *      {
 *        type: 'info',
 *        message: 'Info Message'
 *      }
 *    ],
 *    category: 'PAGE'
 *    context: 'app/primary_2'
 * });
 *
 * @example <caption>Example for add property messages.</caption>
 * addMessages({
 *    messages: [
 *      {
 *        type: 'info',
 *        message: 'Info Message'
 *      }
 *    ],
 *    property: '.lastName'
 *    context: 'app/primary_2'
 * });
 *
 *
 */
export const addMessages = (config: MessagesConfigObject) => {
  const { messages, property, pageReference, category, context } = config;
  const payload: Action['payload'] = { messages, context };

  if (property) {
    payload.property = property;
    payload.pageReference = pageReference;
  } else payload.category = category;

  StateManager.dispatch({
    type: ADD_MESSAGES,
    payload
  });
};

export const getAllMessagesPaths = (validationMessages: object, currentPath: string[], allPaths: string[][]) => {
  for (const key of Object.keys(validationMessages)) {
    currentPath.push(key);
    if (key === constants.SUMMARY_OF_MESSAGES) {
      allPaths.push([...currentPath]);
    } else {
      const typeOfKey = typeof validationMessages[key as keyof typeof validationMessages];

      if (typeOfKey === 'object') {
        getAllMessagesPaths(validationMessages[key as keyof typeof validationMessages], currentPath, allPaths);
      }
    }
    currentPath.pop();
  }
  return allPaths;
};

export const getContentFieldsPath = (storeDataObj: any) => {
  let contentFieldsPath = MESSAGES_PATH.CONTENT_FIELDS_PATH;
  if (storeDataObj?.objectInfo) {
    const isObjectType = CaseUtils.isObjectCaseType(storeDataObj?.objectInfo?.objectTypeID);
    contentFieldsPath = isObjectType ? MESSAGES_PATH.OBJECT_CONTENT_FIELDS_PATH : contentFieldsPath;
  } else if (storeDataObj?.dataInfo?.content) {
    contentFieldsPath = MESSAGES_PATH.DATA_OBJECT_CONTENT_FIELDS_PATH;
  }
  return contentFieldsPath;
};

/**
 * this function helps to retrieve all error messages from context object
 * @function getValidationErrorMessages
 * @public
 * @property context - (required) context where messages are stored
 * @returns - error messages
 *
 * @example <caption>Example for retrieving error messages.</caption>
 * context - required
 * getValidationErrorMessages('app/primary_2');
 * @param localizationService - LocalizationService is responsible for providing localized string
 * @example <caption>getValidationErrorMessages('app/primary_2', getPConnect().getLocalizationService());</caption>
 */
export const getValidationErrorMessages = (context: string, localizationService?: LocalizationService) => {
  const storeDataObj = StateManager.getDataObject(context);
  const contentFieldsPath = getContentFieldsPath(storeDataObj);
  const properties = Utils.getIn(storeDataObj, [...contentFieldsPath]) || {};
  const errors: ({ label: string; description: string } | string)[] = [];

  const messagePaths = getAllMessagesPaths(properties, [], []);

  messagePaths.forEach((path) => {
    const propertyMessages = Utils.getIn(properties, [...path, constants.PROPERTY_MESSAGES]);
    if (propertyMessages) {
      Object.keys(propertyMessages).forEach((property) => {
        propertyMessages[property].forEach((error: { type: string; fieldLabel: string; message: string }) => {
          if (error.type === constants.MESSAGES.MESSAGES_TYPE_ERROR) {
            const localizedMessage: string = localizationService?.getLocalizedMessage(error.message) ?? error.message;
            if (error.fieldLabel) {
              errors.push({
                label: `${error.fieldLabel}:`,
                description: localizedMessage
              });
            } else {
              errors.push(localizedMessage);
            }
          }
        });
      });
    }
  });

  return errors;
};

/**
 * this function helps to retrieve messages for Rootcontainer from root context or active container context
 * @function getRootContainerHttpMessages
 * @private
 * @param isNonPortalRootContainer - if renderMode is other than portal then true else false
 * @param activeContainerItemName - active container item name
 * @returns - messages object
 *
 * @example <caption>Example for retrieving http messages.</caption>
 * isNonPortalRootContainer - required
 * activeContainerItemName - optional
 * getRootContainerHttpMessages(true,'app/primary_1');
 */
export const getRootContainerHttpMessages = (
  isNonPortalRootContainer: boolean,
  activeContainerItemName: string
): object => {
  let httpMessages = getMessages({
    context: constants.APP.ROOT,
    category: 'HTTP'
  });
  if (environmentInfo.getRenderingMode() === constants.EMBED) {
    const appMessages = getMessages({
      context: constants.APP.APP,
      category: 'HTTP'
    });
    if (appMessages) {
      httpMessages = httpMessages ? httpMessages.concat(appMessages) : appMessages;
    }
  }
  if (isNonPortalRootContainer) {
    const activeContainerHttpMessages = getMessages({
      context: activeContainerItemName,
      category: 'HTTP'
    });
    if (httpMessages) {
      if (activeContainerHttpMessages) {
        httpMessages = httpMessages.concat(
          getMessages({
            context: activeContainerItemName,
            category: 'HTTP'
          })
        );
      }
    } else {
      httpMessages = getMessages({
        context: activeContainerItemName,
        category: 'HTTP'
      });
    }
  }
  return httpMessages;
};

export const clearContextMessagesInActiveWorkArea = (context: string) => {
  let activeWorkArea: string | null = null;
  if (getChildContainers(context)[0]) {
    activeWorkArea = getActiveContainerItemName(getChildContainers(context)[0]);
  } else {
    activeWorkArea = context;
  }
  if (activeWorkArea) {
    clearMessages({
      context: activeWorkArea,
      stateObj: StateManager.getDataObject(activeWorkArea),
      clearContextMessages: true
    });
  }
};

export const isCustomPageErrorExist = (context: string) => {
  const messages: MessagesObject[] =
    Utils.getIn(StateManager.getDataObject(context), MESSAGES_PATH.PAGE_MESSAGES_PATH) ?? [];

  return messages.some((message) => message.subType === constants.CUSTOM_ERROR);
};

/**
 * Clears all context-specific messages.
 *
 * @param config - Configuration object containing the context to clear messages for.
 * @param config.context - The context for which to clear messages.
 * @returns void
 */
export const clearContextMessages = (config: Pick<MessagesConfigObject, 'context'>): void => {
  const { context } = config;
  StateManager.dispatch({
    type: CLEAR_MESSAGES,
    payload: {
      clearAll: true,
      context
    }
  });
};

const MessageManager = {
  addMessages,
  getValidationErrorMessages,
  getMessages,
  clearMessages,
  getRootContainerHttpMessages,
  clearContextMessagesInActiveWorkArea,
  clearContextMessages,
  isCustomPageErrorExist
};

export default MessageManager;
